import { color, cssColorsNew } from '@lessonup/ui-components';
import { generateText, JSONContent } from '@tiptap/core';
import { fillInTheBlanksViewerExtensions } from '../FillInTheBlanksTiptapViewer';

export interface AnswerModel {
  answers: (string | null)[];
}

interface Answer {
  wordBankIndex: number;
  answerIndex: number;
  content: JSONContent;
}

export type FillInTheBlanksTempAnswerModel = Record<number, Answer>;

export const convertAnswersToAnswerModel = (
  answers: FillInTheBlanksTempAnswerModel,
  posSet: Set<number>
): AnswerModel => {
  const sortedPos = posSetToSortedPos(posSet);
  const answer = sortedPos.map((pos) => {
    const answer = answers[pos];
    if (!answer) return null;
    return generateText(answer.content, fillInTheBlanksViewerExtensions);
  });
  return { answers: answer };
};

/**
 * Sorts the positions in the posSet in ascending order
 */
export const posSetToSortedPos = (posSet: Set<number>) => {
  return Array.from(posSet).sort((a, b) => a - b);
};

/**
 * Styling helper functions for blank nodes
 */
export const getTextColor = (isResultsPhase: boolean, isEditing: boolean, isAnswerCorrect: boolean): string => {
  if (isResultsPhase) {
    return isAnswerCorrect ? color.hierarchy.secondary.fill : color.message.negative.on;
  }

  if (isEditing) {
    return color.hierarchy.primary.fill;
  }

  return color.state.disabled.on;
};

export const getBorderStyle = (isResultsPhase: boolean, isEditing: boolean, isActive: boolean): string | undefined => {
  if (isResultsPhase) {
    return undefined;
  }

  if (isEditing) {
    return `2px solid ${isActive ? cssColorsNew.secondary.stroke.light : color.neutralNew.surface.stroke}`;
  }

  return '2px solid #D6D6D6';
};

export const getBackgroundColor = (
  isResultsPhase: boolean,
  isActive: boolean,
  isEditing: boolean,
  hasAnswer: boolean
): string => {
  if (!isResultsPhase && isActive && isEditing) {
    return color.state.active.fill;
  }

  if (hasAnswer) {
    return color.neutral.surface.background;
  }

  return color.neutral.surface1.background;
};

export const mapWordsToWordBankItems = (
  words: JSONContent[]
): Array<{
  type: 'blank';
  word: JSONContent;
  id: string;
}> => {
  return words.map((word, index) => ({
    type: 'blank' as const,
    word,
    id: index.toString(),
  }));
};

export const getWordBankIndex = (item: { type: string; id?: string }): number | null => {
  if (item.type !== 'blank' || !item.id) {
    return null;
  }
  const index = parseInt(item.id, 10);
  return isNaN(index) ? null : index;
};
